import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import showdown from 'showdown';
import { Faq } from '../../../models/Faq';
import { FaqService } from '../../../services/faq/faq.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-faq-questions',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    FormsModule,
  ],
  templateUrl: './faq-questions.component.html',
  styleUrl: './faq-questions.component.scss',
})
export class FaqQuestionsComponent implements OnInit {
  faqsList: Faq[] = [];
  expandedIndexes: number[] = [];
  converter = new showdown.Converter();
  searchTerm: string = '';
  private searchSubject = new Subject<string>();

  constructor(
    private faqService: FaqService,
    private sanitizer: DomSanitizer
  ) {
    this.converter.setOption('simpleLineBreaks', true);
    this.converter.setOption('simplifiedAutoLink', true);
  }

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchTerm = searchTerm;
      this.searchFaqs(searchTerm);
    });

    this.searchFaqs();
  }

  updateFieldText(event: Event) {
    const query = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.searchSubject.next(query);
  }

  searchFaqs(filter?: string) {
    this.faqService.getFaqs(filter).subscribe((data) => {
      this.faqsList = data;

      if (filter) {
        this.expandedIndexes = this.faqsList
          .map((faq, i) =>
            faq.titulo.toLowerCase().includes(filter) ||
            faq.descricao.toLowerCase().includes(filter)
              ? i
              : -1
          )
          .filter((i) => i !== -1);
      } else {
        this.expandedIndexes = [];
      }
    });
  }

  toggleExpanded(index: number): void {
    const currentIndex = this.expandedIndexes.indexOf(index);
    if (currentIndex > -1) {
      this.expandedIndexes.splice(currentIndex, 1);
    } else {
      this.expandedIndexes.push(index);
    }
  }

  convertMarkdown(markdownText: string): SafeHtml {
    let html = this.converter.makeHtml(markdownText);

    if (this.searchTerm) {
      const keywords = this.searchTerm.trim().toLowerCase().split(/\s+/);
      keywords.forEach(keyword => {
        const escaped = this.escapeRegExp(keyword);
        const regex = new RegExp(`(\\b${escaped}\\b)`, 'gi');
        html = html.replace(regex, `<span class="highlighted">$1</span>`);
      });
    }

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  highlightText(text: string): SafeHtml {
    if (!this.searchTerm) return this.sanitizer.bypassSecurityTrustHtml(text);

    const keywords = this.searchTerm.trim().toLowerCase().split(/\s+/);
    let highlightedText = text;

    keywords.forEach(keyword => {
      const escapedKeyword = this.escapeRegExp(keyword);
      const regex = new RegExp(`(\\b${escapedKeyword}\\b)`, 'gi');
      highlightedText = highlightedText.replace(
        regex,
        `<span class="highlighted">$1</span>`
      );
    });

    return this.sanitizer.bypassSecurityTrustHtml(highlightedText);
  }
}
